import {
  AlertTriangle,
  ArrowLeft,
  CheckCircle,
  Copy,
  Loader2,
  XCircle,
} from "lucide-react";
import { LIJJAT_BRAND, getLijjatProductImage } from "./lijjatBrandAssets";

const statusConfig = {
  auth: {
    icon: <CheckCircle className="h-5 w-5 text-emerald-600" />,
    title: "Original Product",
    boxClassName: "border-[#FFE699] bg-[#FFE699]",
    textClassName: "text-gray-800",
  },
  unauth: {
    icon: <XCircle className="h-5 w-5 text-red-600" />,
    title: "Unauthentic Product",
    boxClassName: "border-red-300 bg-red-100",
    textClassName: "text-red-700",
  },
  duplicate: {
    icon: <Copy className="h-5 w-5 text-orange-600" />,
    title: "Duplicate Scan Detected",
    boxClassName: "border-orange-300 bg-orange-100",
    textClassName: "text-orange-700",
  },
};

const messageConfig = {
  unauth: {
    boxClassName: "bg-red-100",
    textClassName: "text-red-700",
    icon: <XCircle className="h-5 w-5 text-red-600" />,
  },
  duplicate: {
    boxClassName: "bg-orange-100",
    textClassName: "text-orange-700",
    icon: <Copy className="h-5 w-5 text-orange-600" />,
  },
  default: {
    boxClassName: "bg-gray-100",
    textClassName: "text-gray-700",
    icon: <AlertTriangle className="h-5 w-5 text-gray-600" />,
  },
};

const formatCurrency = (value) => {
  if (value === undefined || value === null || value === "") {
    return "N/A";
  }

  return `Rs. ${value}`;
};

const DetailsTable = ({ title, rows }) => (
  <div className="min-w-0 flex-1 overflow-hidden rounded-lg border border-gray-400">
    <div className="border-b-2 border-gray-400 bg-[#d35400] px-4 py-2">
      <h3 className="text-center text-base font-semibold text-white">{title}</h3>
    </div>
    <div className="w-full text-sm">
      {rows.map((row, index) => (
        <div
          key={`${row.label}-${index}`}
          className={`flex border-t border-gray-400 ${
            index % 2 === 0 ? "bg-[#FFE699]" : "bg-[#BDD7EE]"
          }`}
        >
          <div className="w-2/5 border-r border-gray-400 p-2 text-xs font-semibold uppercase text-gray-700">
            {row.label}
          </div>
          <div className="w-3/5 break-words p-2 text-sm text-gray-900">
            {Array.isArray(row.value)
              ? row.value.map((line) => <p key={line}>{line}</p>)
              : row.value || "N/A"}
          </div>
        </div>
      ))}
    </div>
  </div>
);

const StatusCard = ({ status }) => {
  if (!statusConfig[status]) {
    return null;
  }

  const currentStatus = statusConfig[status];

  return (
    <div
      className={`mx-4 mb-4 flex items-center justify-center rounded-lg border p-3 ${currentStatus.boxClassName}`}
    >
      <div className="flex items-center gap-2">
        <div className="rounded-full bg-white p-2">{currentStatus.icon}</div>
        <h3 className={`text-center text-sm font-bold ${currentStatus.textClassName}`}>
          {currentStatus.title}
        </h3>
      </div>
    </div>
  );
};

const MessageCard = ({ status, message }) => {
  if (!message) {
    return null;
  }

  const currentMessage = messageConfig[status] || messageConfig.default;

  return (
    <div className={`mx-4 mb-4 flex items-center gap-2 rounded-lg p-3 ${currentMessage.boxClassName}`}>
      {currentMessage.icon}
      <p className={`text-sm font-medium ${currentMessage.textClassName}`}>{message}</p>
    </div>
  );
};

const LijjatProductDetailsView = ({
  productData,
  locationData,
  status,
  loading,
  message,
  onBack,
}) => {
  const showProduct = status === "auth" && productData && !loading;

  return (
    <div className="min-h-screen bg-white">
      <header className="px-4 pt-6 pb-2">
        <div className="flex items-center justify-between gap-3">
          {onBack ? (
            <button
              onClick={onBack}
              className="rounded-full bg-white p-2 shadow-md transition-shadow hover:shadow-lg"
              aria-label="Go back"
            >
              <ArrowLeft className="h-5 w-5 text-[#d35400]" />
            </button>
          ) : (
            <div className="w-10" />
          )}

          <div className="flex flex-1 flex-col items-center md:flex-row md:justify-center md:gap-3">
            <img
              src={LIJJAT_BRAND.logo}
              alt="Lijjat Logo"
              className="h-14 w-auto object-contain"
            />
            <h1 className="mt-1 max-w-[220px] text-center text-sm font-semibold text-[#d35400] md:mt-0 md:max-w-none md:text-left md:text-base lg:text-lg">
              {LIJJAT_BRAND.companyName}
            </h1>
          </div>

          <div className="w-10" />
        </div>
      </header>

      {/* <div className="pt-4">
        <StatusCard status={status} />
      </div>

      <MessageCard status={status} message={message} /> */}

      {loading && (
        <div className="flex flex-col items-center justify-center py-12">
          <Loader2 className="mb-4 h-8 w-8 animate-spin text-gray-600" />
          <p className="font-medium text-gray-600">Verifying product...</p>
        </div>
      )}

      {showProduct && (
        <div className="mx-4 mb-6">
          <div className="mb-6 flex justify-center">
            <img
              src={getLijjatProductImage(productData.productCode)}
              alt={productData.productName || "Product"}
              className="h-40 w-40 rounded-md bg-[#FFE699] p-3 object-contain shadow-sm"
            />
          </div>

          <div className="flex flex-col gap-6 md:flex-row">
            <DetailsTable
              title="Product Details"
              rows={[
                { label: "Product Name", value: productData.productName },
                { label: "Batch No.", value: productData.batchNumber },
                { label: "Unit Code", value: productData.batchName },
                { label: "Mfg Date", value: productData.manufacturingDate },
                { label: "Exp Date", value: productData.expiryDate },
                { label: "MRP", value: formatCurrency(productData.mrp) },
              ]}
            />

            {locationData && (
              <DetailsTable
                title="Manufacturing Details"
                rows={[
                  { label: "Branch Name", value: locationData.branchName },
                  { label: "Manufactured By", value: locationData.manufacturedBy },
                  {
                    label: "Address",
                    value: [
                      locationData.street1,
                      locationData.street2,
                      locationData.street3,
                      locationData.street4,
                      locationData.street5,
                      locationData.street6,
                      locationData.state,
                    ].filter(Boolean),
                  },
                  { label: "FSSAI License No", value: locationData.fssaiNo },
                  { label: "Customer Care", value: locationData.customerCare },
                ]}
              />
            )}
          </div>
        </div>
      )}

      <footer className="mt-4 border-t-2 border-gray-400 px-4 py-6 text-center">
        <p className="text-xs text-gray-500">
          Developed by: Shubham Automation Pvt. Ltd.
        </p>
      </footer>
    </div>
  );
};

export default LijjatProductDetailsView;
